import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { formatCurrency } from '@/js/shared/billables/currency';
import { calculateSectionTotals } from '@/js/shared/billables/section_totals';
import Meta from '@/js/util/meta';

export default class extends Controller {
  static targets = [
    'toggle',
    'toggleIcon',
    'count',
    'name',
    'nameInput',
    'nameError',
    'totals',
    'subtotal',
    'marginAmount',
    'marginPercentage',
    'marginValue',
    'marginUnavailable',
    'marginInfo',
  ];
  static outlets = ['billable-section-delete-dialog'];
  static values = { url: String, name: String };
  #skipCommit = false;
  #saving = false;
  #deleting = false;

  connect() {
    if (this.#pending) {
      setTimeout(() => this.nameInputTarget.focus({ preventScroll: true }));
    }
  }

  toggle(event) {
    event.preventDefault();
    this.#setExpanded(!this.element.classList.contains('expanded'));
  }

  expandAll() {
    this.#setExpanded(true);
  }

  collapseAll() {
    this.#setExpanded(false);
  }

  syncCount() {
    if (!this.hasCountTarget) {
      return;
    }

    this.countTarget.textContent = this.element.querySelectorAll('.billable-item').length;
  }

  syncTotals() {
    if (!this.hasTotalsTarget) {
      return;
    }

    const rows = [...this.element.querySelectorAll('.billable-item')];
    const totals = calculateSectionTotals(this.#itemControllers(rows));
    const currency = { symbol: Meta.get('currency-symbol'), side: Meta.get('currency-side') };

    this.totalsTarget.hidden = rows.length === 0;
    this.subtotalTarget.textContent = formatCurrency(totals.subtotal, currency);
    if (this.hasMarginAmountTarget) {
      this.marginAmountTarget.textContent = formatCurrency(totals.margin, currency);
      this.marginPercentageTarget.textContent = totals.marginPercentage;
      this.#toggleMarginAvailability(totals.marginAvailable);
    }
  }

  #toggleMarginAvailability(available) {
    this.marginValueTarget.hidden = !available;
    this.marginUnavailableTarget.hidden = available;

    if (
      !available &&
      this.hasMarginInfoTarget &&
      !this.marginInfoTarget._tippy &&
      typeof window.initTooltips === 'function'
    ) {
      window.initTooltips(this.marginInfoTarget);
    }
  }

  startRename() {
    this.element.classList.add('billable-section--editing');
    this.nameInputTarget.value = this.nameValue;
    this.#clearError();
    this.nameInputTarget.focus();
    this.nameInputTarget.select();
  }

  nameInputKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitName();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.#cancelEdit();
    }
  }

  commitName() {
    if (this.#skipCommit) {
      return;
    }

    const name = this.nameInputTarget.value.trim();
    if (!this.#pending && name === this.nameValue) {
      this.#exitEditing();
      return;
    }

    if (name === '') {
      this.#showError();
      return;
    }

    this.#clearError();
    if (this.#pending) {
      this.#createSection(name);
    } else {
      this.#renameSection(name);
    }
  }

  openDeleteDialog() {
    this.billableSectionDeleteDialogOutlet.open({
      sectionName: this.nameValue,
      onConfirm: () => this.destroy(),
    });
  }

  async destroy() {
    if (this.#deleting) {
      return;
    }
    this.#deleting = true;

    const request = new FetchRequest('delete', this.urlValue, { responseKind: 'json' });

    try {
      const response = await request.perform();
      if (response.ok) {
        this.#removeSection();
      } else {
        window.alert('The section could not be deleted. Please try again.');
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#deleting = false;
    }
  }

  async #createSection(name) {
    if (this.#saving) {
      return;
    }
    this.#saving = true;
    this.element.classList.add('billable-section--saving');

    const body = new FormData();
    body.append('name', name);
    const request = new FetchRequest('post', this.urlValue, { body, responseKind: 'turbo-stream' });

    try {
      const response = await request.perform();
      if (!response.ok && !response.unprocessableEntity) {
        this.#createFailed();
      } else {
        this.#skipCommit = true;
      }
    } catch (error) {
      console.error(error);
      this.#createFailed();
    } finally {
      this.#saving = false;
    }
  }

  #createFailed() {
    this.element.classList.remove('billable-section--saving');
    this.#showError();
    this.dispatch('create-failed');
  }

  async #renameSection(name) {
    if (this.#saving) {
      return;
    }
    this.#saving = true;

    const body = new FormData();
    body.append('name', name);
    const request = new FetchRequest('patch', this.urlValue, { body, responseKind: 'json' });

    try {
      const response = await request.perform();
      if (!response.ok) {
        this.#showError();
        return;
      }

      this.nameValue = name;
      this.nameTarget.textContent = name;
      this.#exitEditing();
    } catch (error) {
      console.error(error);
      this.#showError();
    } finally {
      this.#saving = false;
    }
  }

  #cancelEdit() {
    if (this.#pending) {
      this.#skipCommit = true;
      this.element.remove();
      return;
    }

    this.#skipCommit = true;
    this.nameInputTarget.blur();
    this.#skipCommit = false;
    this.#exitEditing();
  }

  #exitEditing() {
    this.element.classList.remove('billable-section--editing');
    this.#clearError();
  }

  #removeSection() {
    const rows = [...this.element.querySelectorAll(':scope > .billable-item')];
    const grid = this.element.parentElement;
    const destination = grid?.querySelector('.billable-section--unassigned') ?? grid;
    if (rows.length && destination) {
      destination.append(...rows);
      this.dispatch('items-moved', { target: destination });
    }
    this.element.remove();
  }

  #itemControllers(rows) {
    return rows
      .map((row) => this.application.getControllerForElementAndIdentifier(row, 'billable-item'))
      .filter((item) => item && !item.markedForDestruction);
  }

  #showError() {
    this.nameInputTarget.classList.add('billable-section__name-input--invalid');
    this.nameErrorTarget.hidden = false;
  }

  #clearError() {
    this.nameInputTarget.classList.remove('billable-section__name-input--invalid');
    this.nameErrorTarget.hidden = true;
  }

  #setExpanded(expanded) {
    this.element.classList.toggle('expanded', expanded);
    this.toggleTarget.setAttribute('aria-expanded', expanded);
    this.toggleIconTarget.classList.toggle('fa-caret-down', expanded);
    this.toggleIconTarget.classList.toggle('fa-caret-right', !expanded);
    this.dispatch('toggled', { detail: { expanded } });
  }

  get #pending() {
    return this.element.classList.contains('billable-section--pending');
  }
}
